import { useMemo } from 'react';
import getConfig from 'next/config';
import { createContext, useContextSelector } from '../utils/ContextSelector';
import { useAnalyticsDataInitialiser } from './initialisers/useAnalyticsDataInitialiser';
import { useAnchorCardDataInitialiser } from './initialisers/useAnchorCardDataInitialiser';
import { useBreadcrumbDataInitialiser } from './initialisers/useBreadcrumbDataInitialiser';
import { useCompareDataInitialiser } from './initialisers/useCompareDataInitialiser';
import { useDefaultDataInitialiser } from './initialisers/useDefaultDataInitialiser';
import { useEnergyDrawDataInitialiser } from './initialisers/useEnergyDrawDataInitialiser';
import { useHeaderPeepingDataInitialiser } from './initialisers/useHeaderPeepingDataInitialiser';
import { usePlpBodySentinelInitialiser } from './initialisers/usePlpBodySentinelInitialiser';
import { useProductsDataInitialiser } from './initialisers/useProductsDataInitialiser';
import { useSearchDataInitialiser } from './initialisers/useSearchDataInitialiser';
import { useRouter } from 'next/router';

export const Context = createContext();

export const useAnalyticsContext = () =>
  useContextSelector(Context, (o) => o.analytics);
export const useAnchorCardContext = () =>
  useContextSelector(Context, (o) => o.anchorCard);
export const useApplicationSettingsContext = () =>
  useContextSelector(Context, (o) => o.applicationSettings);
export const useBannerContext = () =>
  useContextSelector(Context, (o) => o.banners);
export const useBreadcrumbContext = () =>
  useContextSelector(Context, (o) => o.breadcrumbs);
export const useCompareContext = () =>
  useContextSelector(Context, (o) => o.compare);
export const useCookieContext = () =>
  useContextSelector(Context, (o) => o.cookies);
export const useEnergyDrawerContext = () =>
  useContextSelector(Context, (o) => o.energyDrawer);
export const useFacetContext = () =>
  useContextSelector(Context, (o) => o.facets);
export const useHeaderPeepingContext = () =>
  useContextSelector(Context, (o) => o.headerPeeping);
export const usePlpBodySentinelContext = () =>
  useContextSelector(Context, (o) => o.plpBodySentinel);
export const useProductContext = () =>
  useContextSelector(Context, (o) => o.products);
export const useRequestContext = () =>
  useContextSelector(Context, (o) => o.pageCorrelationId);
export const useRouterContext = () =>
  useContextSelector(Context, (o) => o.router);
export const useSearchContext = () =>
  useContextSelector(Context, (o) => o.search);
export const useVertexContext = () =>
  useContextSelector(Context, (o) => o.vertex);

export const useFoundItLinksDataContext = () =>
  useContextSelector(Context, (o) => o.foundItLinks);

export const StateProvider = ({
  providerData = {},
  cookies,
  children,
  foundItLinks,
}) => {
  const { publicRuntimeConfig } = useMemo(() => getConfig(), []);
  const router = useRouter();

  const parsedData = useMemo(() => JSON.parse(providerData), [providerData]);

  const analyticsData = useAnalyticsDataInitialiser();
  const anchorCardData = useAnchorCardDataInitialiser();
  const bannersData = useDefaultDataInitialiser(parsedData.banners);
  const breadCrumbData = useBreadcrumbDataInitialiser(parsedData.breadcrumbs);
  const compareData = useCompareDataInitialiser();
  const cookieData = useDefaultDataInitialiser(cookies);
  const energyDrawerData = useEnergyDrawDataInitialiser();
  const facetsData = useDefaultDataInitialiser(parsedData.facets);
  const foundItLinksData = useDefaultDataInitialiser(foundItLinks || null);
  const headerPeepingData = useHeaderPeepingDataInitialiser();
  const pageCorrelationData = useDefaultDataInitialiser(
    parsedData.pageCorrelationId || ''
  );
  const plpBodySentinelData = usePlpBodySentinelInitialiser();
  const productsData = useProductsDataInitialiser(parsedData.products);
  const routerData = useDefaultDataInitialiser(router);
  const searchData = useSearchDataInitialiser(parsedData.search, routerData);
  const vertexData = useDefaultDataInitialiser(parsedData.vertex);

  return (
    <Context.Provider
      value={{
        analytics: analyticsData,
        anchorCard: anchorCardData,
        applicationSettings: publicRuntimeConfig,
        banners: bannersData,
        breadcrumbs: breadCrumbData,
        compare: compareData,
        cookies: cookieData,
        energyDrawer: energyDrawerData,
        facets: facetsData,
        foundItLinks: foundItLinksData,
        headerPeeping: headerPeepingData,
        pageCorrelationId: pageCorrelationData,
        plpBodySentinel: plpBodySentinelData,
        products: productsData,
        router: routerData,
        search: searchData,
        vertex: vertexData,
      }}
    >
      {children}
    </Context.Provider>
  );
};
export default StateProvider;
